import { useState, useEffect, useMemo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { addWeeks } from 'date-fns'
import { getEnergyLevelData } from '../services'
import { EnergyLevelData } from '../types'

interface UseEnergyLevelDataProps {
  referenceTimestamp?: string
  isFeatureEnabled: boolean
}

interface UseEnergyLevelDataReturn {
  energyData: EnergyLevelData | null
  loading: boolean
  error: string | null
  isForbidden: boolean
  refetch: () => Promise<void>
  silentRefetch: () => Promise<void>
}

// Calculate default timestamp once at module level
let defaultTimestamp: string | null = null
const getDefaultTimestamp = (): string => {
  if (!defaultTimestamp) {
    const eightWeeksAgo = addWeeks(new Date(), -8)
    defaultTimestamp = eightWeeksAgo.toISOString()
  }
  return defaultTimestamp
}

/**
 * Custom hook for fetching energy level data
 * Handles loading states, errors, and data caching
 */
export const useEnergyLevelData = ({ referenceTimestamp, isFeatureEnabled }: UseEnergyLevelDataProps): UseEnergyLevelDataReturn => {
  const { formatMessage } = useIntl()
  const [energyData, setEnergyData] = useState<EnergyLevelData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isForbidden, setIsForbidden] = useState(false)

  // Memoize timestamp to prevent recalculation
  const timestamp = useMemo(() => {
    return referenceTimestamp || getDefaultTimestamp()
  }, [referenceTimestamp])

  const fetchData = useCallback(
    async (silent = false) => {
      if (!isFeatureEnabled) {
        return
      }

      try {
        if (!silent) {
          setLoading(true)
        }
        setError(null)
        const data = await getEnergyLevelData(timestamp, formatMessage)
        setEnergyData(data)
      } catch (err) {
        if (err instanceof Error && err.message === 'NO_DATA_AVAILABLE') {
          setError(formatMessage({ id: 'energyLevel.noDataTitle' }))
        } else if (err && typeof err === 'object' && (err as Record<string, unknown>).message === 'AccessForbidden') {
          setIsForbidden(true)
        } else {
          console.error('Error fetching energy level data:', err)
          setError(formatMessage({ id: 'energyLevel.errorMessage' }))
        }
      } finally {
        if (!silent) {
          setLoading(false)
        }
      }
    },
    [timestamp, isFeatureEnabled, formatMessage]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    energyData,
    loading,
    error,
    isForbidden,
    refetch: fetchData,
    silentRefetch: () => fetchData(true),
  }
}
